import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";

/**
 * Aviso al reactivar: portales de la agencia en los que la ficha no está activa
 * (baja previa o nunca marcada). Los que ya están ON no vienen en la lista.
 *
 * items: [{ propiedadId, referencia, portales: [{ portal_id, slug, nombre, color, fecha_baja, activo }] }]
 */
export default function ModalRepublicarPortales({
  isOpen,
  items = [],
  onClose,
  onDone,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);

  const flatKeys = useMemo(() => {
    const keys = [];
    for (const it of items) {
      for (const p of it.portales || []) {
        keys.push(`${it.propiedadId}::${p.portal_id}`);
      }
    }
    return keys;
  }, [items]);

  useEffect(() => {
    if (!isOpen) return;
    setSeleccion(new Set(flatKeys));
    setError("");
    setResultado(null);
    setEnviando(false);
  }, [isOpen, flatKeys]);

  if (!isOpen || !items.length) return null;

  const toggleKey = (key) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = (on) => {
    setSeleccion(on ? new Set(flatKeys) : new Set());
  };

  const fmtFecha = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const publicar = async () => {
    const pairs = [...seleccion].map((k) => {
      const [propiedadId, portalId] = k.split("::");
      return { propiedadId, portalId: Number(portalId) || portalId };
    });
    if (!pairs.length) {
      setError("Selecciona al menos un portal, o pulsa «Ahora no».");
      return;
    }
    setEnviando(true);
    setError("");
    const ok = [];
    const fail = [];
    for (const { propiedadId, portalId } of pairs) {
      try {
        await api.post("/portales/propiedades/toggle", {
          propiedad_id: propiedadId,
          portal_id: portalId,
          activo: true,
        });
        ok.push(`${propiedadId}/${portalId}`);
      } catch (e) {
        fail.push({
          propiedadId,
          portalId,
          error: e.response?.data?.error || e.message || "Error",
        });
      }
    }
    setEnviando(false);
    setResultado({ ok: ok.length, fail });
    if (!fail.length) {
      onDone?.({ ok: ok.length, fail });
      onClose?.();
    }
  };

  const multi = items.length > 1;
  const titulo = multi
    ? "Republicar en portales"
    : `Portales previos · ${items[0].referencia || "propiedad"}`;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={() => {
        if (!enviando) onClose?.();
      }}
    >
      <div
        style={{
          background: c.bgPanel,
          borderRadius: 14,
          padding: 22,
          width: "100%",
          maxWidth: 520,
          border: `1px solid ${c.border}`,
          boxShadow: isDark
            ? "0 16px 48px rgba(0,0,0,0.5)"
            : "0 16px 48px rgba(0,0,0,0.12)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            margin: "0 0 8px",
            fontSize: 17,
            fontWeight: 700,
            color: c.textPrimary,
          }}
        >
          {titulo}
        </h3>
        <p
          style={{
            margin: "0 0 16px",
            fontSize: 13,
            lineHeight: 1.45,
            color: c.textSecondary,
          }}
        >
          {multi
            ? "Estas propiedades vuelven a estar activas y no están publicadas en algunos portales. Elige en cuáles quieres publicarlas."
            : "La propiedad vuelve a estar activa y no está publicada en estos portales. ¿Quieres publicarla? Marca los deseados (los que ya están ON no aparecen)."}
        </p>

        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 12,
            fontSize: 12,
          }}
        >
          <button
            type="button"
            onClick={() => toggleAll(true)}
            style={{
              background: "transparent",
              border: "none",
              color: c.accent || c.chipActiveText,
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Marcar todos
          </button>
          <button
            type="button"
            onClick={() => toggleAll(false)}
            style={{
              background: "transparent",
              border: "none",
              color: c.textMuted,
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Desmarcar todos
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {items.map((it) => (
            <div key={it.propiedadId}>
              {multi && (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: c.textPrimary,
                    marginBottom: 6,
                  }}
                >
                  {it.referencia || it.propiedadId}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(it.portales || []).map((p) => {
                  const key = `${it.propiedadId}::${p.portal_id}`;
                  const checked = seleccion.has(key);
                  const baja = fmtFecha(p.fecha_baja);
                  return (
                    <label
                      key={key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: `1px solid ${
                          checked ? c.chipActiveBorder || c.border : c.border
                        }`,
                        background: checked
                          ? c.chipActiveBg || (isDark ? "#1a2332" : "#f0f7ff")
                          : c.bgCard || "transparent",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleKey(key)}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: p.color || c.textMuted,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 13,
                            fontWeight: 600,
                            color: c.textPrimary,
                          }}
                        >
                          {p.nombre || p.slug}
                        </span>
                        {baja && (
                          <span
                            style={{
                              fontSize: 11,
                              color: c.textMuted,
                            }}
                          >
                            Baja el {baja}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              color: c.accentRed || "#dc2626",
            }}
          >
            {error}
          </div>
        )}
        {resultado?.fail?.length > 0 && (
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              color: c.accentRed || "#dc2626",
            }}
          >
            Publicados {resultado.ok}. Fallaron {resultado.fail.length}:{" "}
            {resultado.fail.map((f) => f.error).join("; ")}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
          }}
        >
          <button
            type="button"
            disabled={enviando}
            onClick={() => onClose?.()}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${c.border}`,
              background: "transparent",
              color: c.textSecondary,
              cursor: enviando ? "not-allowed" : "pointer",
              fontSize: 13,
            }}
          >
            Ahora no
          </button>
          <button
            type="button"
            disabled={enviando || seleccion.size === 0}
            onClick={publicar}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: c.chipActiveBg || "#2563eb",
              color: c.chipActiveText || "#fff",
              cursor:
                enviando || seleccion.size === 0 ? "not-allowed" : "pointer",
              fontSize: 13,
              fontWeight: 600,
              opacity: enviando || seleccion.size === 0 ? 0.6 : 1,
            }}
          >
            {enviando
              ? "Publicando…"
              : `Publicar seleccionados (${seleccion.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Carga publicaciones previas de una o varias propiedades.
 * @returns {Promise<Array<{propiedadId, referencia, portales}>>}
 */
export async function cargarItemsPublicacionesPrevias(propiedadIds) {
  const ids = [...new Set((propiedadIds || []).map(String).filter(Boolean))];
  const items = [];
  for (const pid of ids) {
    try {
      const { data } = await api.get(
        `/portales/propiedades/${pid}/publicaciones-previas`
      );
      const portales = Array.isArray(data?.portales) ? data.portales : [];
      if (portales.length) {
        items.push({
          propiedadId: pid,
          referencia: data.referencia || "",
          portales,
        });
      }
    } catch (e) {
      console.warn("[portales previos]", pid, e.message);
    }
  }
  return items;
}
